import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ACK_TEXT = {
  en: "By submitting this form, I confirm that the information is mine and that I am 18 or older. I authorize Fortunate Operations and the participating licensed insurance professional to review and respond to this insurance-information request by email or individualized follow-up. Automated or recurring text messages require the separate optional SMS consent below. Calls using automated dialing, an artificial or prerecorded voice, or an AI-generated or AI-assisted voice require the separate optional calling consent below. Leaving either optional consent unchecked will not prevent submission.",
  es: "Al enviar este formulario, confirmo que la información me pertenece y que tengo 18 años o más. Autorizo a Fortunate Operations y al profesional de seguros con licencia participante a revisar y responder a esta solicitud de información de seguros por correo electrónico o mediante seguimiento individual. Los mensajes de texto automatizados o recurrentes requieren el consentimiento opcional de SMS que aparece a continuación. Las llamadas que utilicen marcación automatizada, una voz artificial o pregrabada, o una voz generada o asistida por IA requieren el consentimiento opcional de llamadas que aparece a continuación. Dejar cualquiera de estos consentimientos opcionales sin marcar no impedirá el envío del formulario.",
};

const T = {
  en: {
    tagline: "Final Expense Quote Request",
    headline: "Request your Final Expense quote",
    intro: "Tell us a little about yourself and a licensed agent will follow up. This is a request only — no coverage, price, or approval is guaranteed.",
    first: "First name", last: "Last name", phone: "Phone", email: "Email", state: "State",
    age: "Age range", coverage: "Requested coverage", select: "Select…",
    sms: "You may text me about my quote (message/data rates may apply, reply STOP to opt out).",
    call: "You may call me about my quote.",
    ack: "I have read and agree to the disclosure above.",
    submit: "Request My Quote", submitting: "Submitting…",
    privacy: "Your information is submitted securely to the agency for follow-up. Consent is not a condition of purchase.",
    success_h: "Thank you — we’ve received your request.",
    success_p: "A licensed agent will follow up with you. Reference #",
    err_ack: "Please read and acknowledge the disclosure to continue.",
    lang: "Language",
  },
  es: {
    tagline: "Solicitud de cotización de gastos finales",
    headline: "Solicite su cotización de gastos finales",
    intro: "Cuéntenos un poco sobre usted y un agente con licencia se comunicará. Esto es solo una solicitud — no se garantiza cobertura, precio ni aprobación.",
    first: "Nombre", last: "Apellido", phone: "Teléfono", email: "Correo electrónico", state: "Estado",
    age: "Rango de edad", coverage: "Cobertura solicitada", select: "Seleccione…",
    sms: "Pueden enviarme mensajes de texto sobre mi cotización (pueden aplicarse tarifas; responda STOP para cancelar).",
    call: "Pueden llamarme sobre mi cotización.",
    ack: "He leído y acepto la divulgación anterior.",
    submit: "Solicitar mi cotización", submitting: "Enviando…",
    privacy: "Su información se envía de forma segura a la agencia para dar seguimiento. El consentimiento no es una condición de compra.",
    success_h: "Gracias — hemos recibido su solicitud.",
    success_p: "Un agente con licencia se comunicará con usted. Referencia #",
    err_ack: "Lea y acepte la divulgación para continuar.",
    lang: "Idioma",
  },
};

export default function CampaignLanding() {
  const { token } = useParams();
  const [cfg, setCfg] = useState(undefined); // undefined=loading, null=not found
  const [lang, setLang] = useState("en");
  const [f, setF] = useState({
    first_name: "", last_name: "", phone: "", email: "", state: "", age_band: "",
    requested_coverage: "", sms_opt_in: false, call_opt_in: false, acknowledge_disclosure: false, website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const tt = useMemo(() => T[lang] || T.en, [lang]);

  useEffect(() => {
    api.get(`/public/campaigns/${token}`)
      .then(({ data }) => { setCfg(data); setLang(data.default_language || "en"); })
      .catch(() => setCfg(null));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.acknowledge_disclosure) { alert(tt.err_ack); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/campaigns/${token}/leads`, { ...f, preferred_language: lang, form_token: cfg.form_token });
      setDone(data);
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (cfg === undefined) return <div className="min-h-screen grid place-items-center bg-[#17324D]"><Loader2 className="h-6 w-6 animate-spin text-white/70" /></div>;
  if (cfg === null) return <div className="min-h-screen grid place-items-center bg-[#17324D] text-slate-300 p-6 text-center" data-testid="cl-notfound">This page isn’t available.</div>;

  const disclosure = ACK_TEXT[lang] || ACK_TEXT.en;

  return (
    <div className="min-h-screen bg-[#17324D] text-slate-100 flex flex-col" data-testid="campaign-landing-page">
      <div className="absolute -top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-[#315A7D]/40 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[#B28A3D]/15 blur-3xl" />

      <div className="relative mx-auto w-full max-w-xl px-6 py-10 flex-1">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#d9b978]">{cfg.agency_name}</div>
          <div className="flex gap-1" data-testid="cl-lang-switch">
            {["en", "es"].map((l) => (
              <button key={l} onClick={() => setLang(l)}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold ${lang === l ? "bg-[#B28A3D] text-white" : "bg-white/10 text-slate-300"}`}
                data-testid={`cl-lang-${l}`}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>

        <div className="mt-3 text-[11px] uppercase tracking-wider text-slate-400">{tt.tagline}</div>
        <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold leading-tight text-white" data-testid="cl-headline">{tt.headline}</h1>
        <p className="mt-3 text-slate-300 text-sm leading-relaxed">{tt.intro}</p>

        {done ? (
          <div className="mt-8 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10" data-testid="cl-success">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            <h2 className="mt-3 font-heading text-xl font-bold text-white">{tt.success_h}</h2>
            {done.reference && <p className="mt-1 text-sm text-slate-300">{tt.success_p}{done.reference}.</p>}
            {cfg.booking_enabled && <Link to={`/book/${token}${done.booking_ref ? `?lead=${done.booking_ref}` : ""}`} className="mt-5 inline-flex rounded-md bg-[#B28A3D] px-4 py-2 text-sm font-semibold text-[#17324D] hover:bg-[#c99b45]" data-testid="cl-book-appointment">
              {lang === "es" ? "Reservar mi cita" : "Book my appointment"}
            </Link>}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="text-slate-200">{tt.first} *</Label><Input required value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-white/90 text-slate-900" data-testid="cl-first" /></div>
              <div><Label className="text-slate-200">{tt.last} *</Label><Input required value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-white/90 text-slate-900" data-testid="cl-last" /></div>
              <div><Label className="text-slate-200">{tt.phone} *</Label><Input required value={f.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white/90 text-slate-900" data-testid="cl-phone" /></div>
              <div><Label className="text-slate-200">{tt.email} *</Label><Input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="bg-white/90 text-slate-900" data-testid="cl-email" /></div>
              <div>
                <Label className="text-slate-200">{tt.state} *</Label>
                <select required value={f.state} onChange={(e) => set("state", e.target.value)} className="mt-1 h-9 w-full rounded-md bg-white/90 px-3 text-sm text-slate-900" data-testid="cl-state">
                  <option value="">{tt.select}</option>
                  {(cfg.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-slate-200">{tt.age}</Label>
                <select value={f.age_band} onChange={(e) => set("age_band", e.target.value)} className="mt-1 h-9 w-full rounded-md bg-white/90 px-3 text-sm text-slate-900" data-testid="cl-age">
                  <option value="">{tt.select}</option>
                  {(cfg.age_bands || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-200">{tt.coverage}</Label>
                <select value={f.requested_coverage} onChange={(e) => set("requested_coverage", e.target.value)} className="mt-1 h-9 w-full rounded-md bg-white/90 px-3 text-sm text-slate-900" data-testid="cl-coverage">
                  <option value="">{tt.select}</option>
                  {(cfg.coverage_ranges || []).map((cv) => <option key={cv} value={cv}>{cv}</option>)}
                </select>
              </div>
            </div>

            {/* honeypot — visually hidden, must stay empty */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
              <label>Website<input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="cl-website" /></label>
            </div>

            <div className="rounded-lg bg-white/5 p-3 text-[11px] leading-relaxed text-slate-300 max-h-40 overflow-y-auto" data-testid="cl-disclosure">
              {disclosure}
              {cfg.disclosure_notice && <div className="mt-2 italic text-slate-400">{cfg.disclosure_notice}</div>}
            </div>

            <label className="flex items-start gap-2 text-[11px] leading-relaxed text-slate-200"><input type="checkbox" className="mt-0.5" checked={f.sms_opt_in} onChange={(e) => set("sms_opt_in", e.target.checked)} data-testid="cl-sms" /><span>{(cfg.sms_consent_text || {})[lang] || tt.sms}{" "}<Link to="/privacy" target="_blank" className="underline hover:text-white">{lang === "es" ? "Privacidad" : "Privacy Policy"}</Link>{" · "}<Link to="/terms" target="_blank" className="underline hover:text-white">{lang === "es" ? "Términos" : "Terms of Service"}</Link>.</span></label>
            <label className="flex items-start gap-2 text-[11px] leading-relaxed text-slate-200"><input type="checkbox" className="mt-0.5" checked={f.call_opt_in} onChange={(e) => set("call_opt_in", e.target.checked)} data-testid="cl-call" /><span>{(cfg.call_consent_text || {})[lang] || tt.call}</span></label>
            <label className="flex items-start gap-2 text-xs text-slate-100 font-medium"><input type="checkbox" required checked={f.acknowledge_disclosure} onChange={(e) => set("acknowledge_disclosure", e.target.checked)} data-testid="cl-ack" /><span>{tt.ack} *</span></label>

            <div className="text-[11px] text-slate-400" data-testid="cl-legal-links">
              <Link to="/privacy" target="_blank" className="underline hover:text-white" data-testid="cl-privacy-link">{lang === "es" ? "Política de Privacidad" : "Privacy Policy"}</Link>
              <span className="mx-2">·</span>
              <Link to="/terms" target="_blank" className="underline hover:text-white" data-testid="cl-terms-link">{lang === "es" ? "Términos del Servicio" : "Terms of Service"}</Link>
              <span className="mx-2">·</span>
              <Link to="/contact" target="_blank" className="underline hover:text-white" data-testid="cl-contact-link">{lang === "es" ? "Contacto" : "Contact"}</Link>
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-[#B28A3D] hover:bg-[#9c7833] text-white" data-testid="cl-submit">
              {submitting ? tt.submitting : tt.submit}
            </Button>
            <p className="text-[11px] text-slate-400">{tt.privacy}</p>
          </form>
        )}

        <div className="mt-8 flex items-start gap-2 text-[11px] text-slate-400">
          <ShieldCheck className="h-4 w-4 shrink-0 text-[#B28A3D]" />
          <span>{lang === "es"
            ? "Fortunate Operations es una agencia de seguros independiente; no suscribe ni emite pólizas de seguro y no está afiliada a ninguna agencia gubernamental."
            : "Fortunate Operations is an independent insurance agency; it does not underwrite or issue insurance and is not affiliated with any government agency."}</span>
        </div>
      </div>

      <div className="relative border-t border-white/10 py-4 text-center text-[11px] text-slate-500" data-testid="cl-footer">
        <div className="mb-2 flex justify-center gap-4">
          <Link to="/privacy" target="_blank" className="hover:text-slate-300" data-testid="cl-footer-privacy">{lang === "es" ? "Privacidad" : "Privacy"}</Link>
          <Link to="/terms" target="_blank" className="hover:text-slate-300" data-testid="cl-footer-terms">{lang === "es" ? "Términos" : "Terms"}</Link>
          <Link to="/contact" target="_blank" className="hover:text-slate-300" data-testid="cl-footer-contact">{lang === "es" ? "Contacto" : "Contact"}</Link>
        </div>
        {lang === "es" ? "Desarrollado por Fortunate Operations" : "Powered by Fortunate Operations"}
      </div>
    </div>
  );
}
